import { computed, ref, type Ref } from "vue";

import { useBrowserStore } from "@/composables/useBrowserStore";

import {
  DEFAULT_LAYOUT_SHORTCUTS,
  layoutToViewState,
  normalizeLayoutShortcuts,
  type PlannerLayout,
  type PlannerLayoutShortcuts,
} from "./planner";

export type PlannerSettings = {
  calendarDate: Ref<Date>;
  isWeekView: Ref<boolean>;
  isWeekendShown: Ref<boolean>;
  isThreeDayView: Ref<boolean>;
  customDays: Ref<number>;
  layoutShortcuts: Ref<PlannerLayoutShortcuts>;
  // Prefix for route-scoped local-storage keys. Child components derive their
  // own sub-keys from this (e.g. `${storagePrefix}:panes/left:state`), so each
  // planner instance remembers its own layout independently.
  storagePrefix: string;
  // Stops the persistence watchers of the synced states above; the calling
  // component owns it (`releaser.defer(settings.release)`).
  release: () => void;
};

/**
 * Per-route planner view state.
 *
 * Every Planner instance gets its OWN refs, so two planners (e.g. on different
 * routes, or briefly overlapping during a `<Suspense>` route transition) can
 * never share—or fight over—the same state. This replaces the former global
 * `UseLocalSettings` singleton.
 *
 * The view-structure flags are persisted to local storage, keyed by both the
 * account (via `useBrowserStore`) and `routeKey` (the route path), so each
 * route remembers its own layout across reloads. `calendarDate` is kept in
 * memory and seeded to "today" on every load — the focused date is not
 * restored across reloads.
 *
 * `defaultLayout` only provides the first-visit defaults: `syncState` uses them
 * solely when nothing has been stored yet, so the user's saved layout for that
 * route wins afterwards.
 */

// Root of the route-scoped local-storage keys of a planner instance. Also
// used to point one planner at another route's stored preferences (see the
// `instanceRoute` prop of Planner.vue).
export const plannerStoragePrefix = (routeKey: string): string =>
  `planner:${routeKey}`;

export const usePlanner = (
  routeKey: string,
  accountId: string,
  defaultLayout?: PlannerLayout
): PlannerSettings => {
  const seed = layoutToViewState(defaultLayout ?? "week");
  const { syncState } = useBrowserStore(accountId);
  const key = (name: string) => `${plannerStoragePrefix(routeKey)}:${name}`;
  const storedLayoutShortcuts = syncState<unknown>(
    key("layouts"),
    DEFAULT_LAYOUT_SHORTCUTS
  );
  storedLayoutShortcuts.value = normalizeLayoutShortcuts(
    storedLayoutShortcuts.value
  );
  const layoutShortcuts = computed<PlannerLayoutShortcuts>({
    get: () => normalizeLayoutShortcuts(storedLayoutShortcuts.value),
    set: (value) => {
      storedLayoutShortcuts.value = normalizeLayoutShortcuts(value);
    },
  });
  const isWeekView = syncState<boolean>(key("isWeekView"), seed.isWeekView);
  const isWeekendShown = syncState<boolean>(
    key("isWeekendShown"),
    seed.isWeekendShown
  );
  const isThreeDayView = syncState<boolean>(
    key("isThreeDayView"),
    seed.isThreeDayView
  );
  const customDays = syncState<number>(key("customDays"), seed.customDays);
  return {
    calendarDate: ref<Date>(new Date()),
    isWeekView,
    isWeekendShown,
    isThreeDayView,
    customDays,
    layoutShortcuts,
    storagePrefix: plannerStoragePrefix(routeKey),
    release: () => {
      storedLayoutShortcuts.release();
      isWeekView.release();
      isWeekendShown.release();
      isThreeDayView.release();
      customDays.release();
    },
  };
};
